import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'
import ListValidationServiceProvider, {
  AddPartsToListResponse,
} from 'src/services/ListValidationServiceProvider'
import {
  EmptyListException,
  CachedPart,
  ValidatedCachedPart,
  ValidationStatus,
  ValidatePart,
  ValidateListError,
  APIErrorResponse,
  ValidateListErrorCodes,
} from './interfaces'
import {
  PartsBin,
  Part,
  ValidationTag,
} from 'src/features/lists/components/AddToList/Common/interfaces'
import _ from 'lodash'
import { StoreInstances } from '../StoreInstancesContainer'
import history from 'src/helpers/history'

enum RemoveStatus {
  CONFIRM,
  ROLLBACK,
}

export enum AddToListStage {
  CHOOSE_METHOD = 1,
  ADD_PROMPT = 2,
  VALIDATE = 3,
  REVIEW = 4,
}

export enum ADD_PROMPT {
  NONE,
  MANUAL,
  IMPORT_FILE,
}

export class AddToListStore {
  private _stage: AddToListStage
  private _selectedPrompt: ADD_PROMPT
  private _validatedListBinId: string
  private _partsBin: PartsBin
  private _runningRemoveJobs: number = 0
  private _error: ValidateListError = null

  constructor() {
    this._stage = AddToListStage.CHOOSE_METHOD
    this._selectedPrompt = ADD_PROMPT.NONE
    makeAutoObservable(this)
  }

  public getStage(): AddToListStage {
    return this._stage
  }

  public setStage(stage: AddToListStage): void {
    this._stage = stage
  }

  public getSelectedPrompt(): ADD_PROMPT {
    return this._selectedPrompt
  }

  public setSelectedPrompt(prompt: ADD_PROMPT): void {
    this._selectedPrompt = prompt
  }

  public nextStage(): AddToListStage {
    this._stage++
    return this._stage
  }

  public previousStage(): AddToListStage {
    this._stage--
    return this._stage
  }

  public isAddToListEnabled(): boolean {
    return this._runningRemoveJobs == 0 && this._partsBin?.parts?.length > 0
  }

  public getError(): ValidateListError {
    return this._error
  }

  public setError(error: ValidateListError): void {
    this._error = error
  }

  public reset(): void {
    this._stage = AddToListStage.CHOOSE_METHOD
    this._selectedPrompt = ADD_PROMPT.NONE
    this.cleanCache(this._validatedListBinId)
    this._validatedListBinId = null
    this._error = null
    // Clear review navigation.
    history.push('/lists')
  }

  public async validateItems(
    listId: number,
    items: ValidatePart[]
  ): Promise<string> {
    if (!items?.length) {
      throw new EmptyListException('addAtLeastOneItemToList')
    }

    return await ListValidationServiceProvider.validateList({
      listId,
      parts: items,
    })
  }

  public async validateFile(listId: number, file: File): Promise<string> {
    const listBinId = await ListValidationServiceProvider.validateFile(
      listId,
      file
    )
    return listBinId
  }

  public async runValidation(validate: () => Promise<string>): Promise<void> {
    try {
      this._error = null
      this.setStage(AddToListStage.VALIDATE) // Show the validate stage screen

      const partsBinID = await validate()
      if (this.getStage() !== AddToListStage.VALIDATE) {
        this.cleanCache(partsBinID)
        return // Users can cancel the validation wizard while the validation request is in progress
      }

      const partsBin = await this.getAllValidatedItems(partsBinID)
      if (this.getStage() !== AddToListStage.VALIDATE) {
        this.cleanCache(partsBinID)
        return // Users can cancel the validation wizard while the getAllValidatedItems function is in progress
      }

      this.setPartsBin(partsBin)
      this.setValidatedListBinId(partsBinID)
      this.setStage(AddToListStage.REVIEW)
    } catch (e: unknown) {
      if (e instanceof ValidateListError) {
        this.setError(e)
      } else {
        const error = e as APIErrorResponse
        const data = error?.response?.data
        const message = ValidateListErrorCodes.get(data?.code)

        this.setError(new ValidateListError(message))
      }
      this.setStage(AddToListStage.ADD_PROMPT)
    }
  }

  public async validateTransientItems(
    listId: number,
    groupName: string,
    items: ValidatePart[]
  ): Promise<Part[]> {
    this._error = null
    const groupedItems = this.groupItems(groupName, items)

    if (!groupedItems?.length) {
      throw new EmptyListException('addAtLeastOneItemToList')
    }

    const cachedParts =
      await ListValidationServiceProvider.validateTransientParts({
        listId,
        parts: groupedItems,
      })

    const validatedParts = cachedParts.map((p) => this.mapValidatedPart(p))

    return validatedParts
  }

  public async getValidatedItems(
    listBinId: string,
    start = 0,
    limit = 1000
  ): Promise<PartsBin> {
    const validatedItems =
      await ListValidationServiceProvider.getValidatedParts(
        listBinId,
        start,
        limit
      )

    const parts = validatedItems.parts.map((p) => this.mapValidatedPart(p))

    return {
      listBinId,
      totalParts: validatedItems.totalParts,
      parts,
    }
  }

  public async getAllValidatedItems(
    partsBinID: string,
    start: number = 0,
    pageSize: number = 100
  ): Promise<PartsBin> {
    const partsBinFragment = await this.getValidatedItems(
      partsBinID,
      start,
      pageSize
    )

    const end = start + partsBinFragment?.parts?.length
    const loadOtherFragment = end < partsBinFragment?.totalParts

    if (loadOtherFragment) {
      const otherFragment = await this.getAllValidatedItems(
        partsBinID,
        end,
        pageSize
      )
      const newParts = otherFragment?.parts || []
      partsBinFragment.parts?.push(...newParts)
    }

    return partsBinFragment
  }

  private mapValidatedPart(item: ValidatedCachedPart): Part {
    const validationTag = this.getValidationTagFromPart(item)
    const lineCode = item.lineCode !== '' ? item.lineCode : 'unknown'
    const description = item.description !== '' ? item.description : 'unknown'
    return {
      seqno: item.seqno,
      imgUrl: item.imageUrl,
      lineCode,
      partNum: item.partNumber,
      description,
      group: item.groupName,
      qty: item.stockQty,
      comments: item.comment,
      isDuplicate: item.isDuplicate,
      validationStatus: item.validationStatus,
      validationTag,
      visible: true,
      originalSeqNo: item.originalSeqNo,
    }
  }

  private getValidationTagFromPart(part: ValidatedCachedPart): ValidationTag {
    if (part?.isDuplicate) return ValidationTag.DUPLICATE

    if (part?.validationStatus === ValidationStatus.INVALID)
      return ValidationTag.INVALID

    return ValidationTag.VALID
  }

  public groupItems(groupName: string, items: ValidatePart[]): ValidatePart[] {
    return items
      .map(
        (i: ValidatePart): ValidatePart => ({
          groupName,
          partNumber: i.partNumber?.trim()?.toUpperCase(),
          lineCode: i.lineCode?.trim()?.toUpperCase(),
          stockQty: i.stockQty,
          comment: i.comment?.trim(),
          originalSeqNo: i.originalSeqNo,
        })
      )
      .filter((i) => i.partNumber !== '')
  }

  public getValidatedListBinId(): string {
    return this._validatedListBinId
  }

  public setValidatedListBinId(listBinId: string): void {
    this._validatedListBinId = listBinId
  }

  public getPartsBin(): PartsBin {
    return this._partsBin
  }

  public setPartsBin(parts: PartsBin): void {
    this._partsBin = parts
  }

  public removePart(seqno: number): void {
    new Promise<RemoveStatus>((resolve) => {
      this._runningRemoveJobs++
      this.setPartVisibility(seqno, false)
      this.deleteNotifcation(resolve)
    }).then((status: RemoveStatus) => {
      if (status == RemoveStatus.CONFIRM) {
        _.remove(this._partsBin?.parts, (p) => p.seqno === seqno) // removing from UI
        this._partsBin.totalParts = this._partsBin.parts.length
        this.removeFromCache(seqno)
      } else {
        this.setPartVisibility(seqno, true)
      }
      this._runningRemoveJobs--
    })
  }

  public setPartVisibility(seqno: number, visible: boolean): void {
    this._partsBin.parts = this._partsBin?.parts.map((p) => {
      return { ...p, visible: p.seqno === seqno ? visible : p.visible }
    })
  }

  private removeFromCache(seqno: number): void {
    ListValidationServiceProvider.removePart(this._validatedListBinId, seqno)
  }

  public cleanCache(listBinId: string): void {
    ListValidationServiceProvider.cleanCache(listBinId)
  }

  private deleteNotifcation(resolve: (RemoveStatus) => void): void {
    const undo = {
      text: 'undo',
      action: () => resolve(RemoveStatus.ROLLBACK),
    }
    StoreInstances.uiStore.displaySuccessNotification(
      'itemSuccessfullyDeleted',
      undo,
      [],
      () => resolve(RemoveStatus.CONFIRM)
    )
  }

  public async updateParts(seqno: number, parts: Part[]): Promise<void> {
    if (!parts?.length) return

    const cachedPart = this.mapToCachedPart(parts[0])
    await ListValidationServiceProvider.updatePart(
      this._validatedListBinId,
      seqno,
      cachedPart
    )

    const partSlice = parts.slice(1)
    const newParts = await this.insertNewParts(partSlice)

    const allParts = [...(this._partsBin?.parts ?? []), ...newParts]

    const updatedParts = await this.syncPartsInfo(this._partsBin, allParts)
    this.setPartsBin({ ...this._partsBin, parts: [...updatedParts] })
  }

  public async syncPartsInfo(
    partsBin: PartsBin,
    parts: Part[]
  ): Promise<Part[]> {
    const updatedPartsBin = await this.getAllValidatedItems(partsBin.listBinId)

    const partsMap = new Map(updatedPartsBin.parts.map((bP) => [bP.seqno, bP]))

    return parts?.map((p) => partsMap.get(p.seqno))
  }

  public async addPartsToList(
    listId: number,
    listBinId: string
  ): Promise<void> {
    const resp: AddPartsToListResponse =
      await ListValidationServiceProvider.addPartsToList(listId, listBinId)

    const numOfItems = resp.totalParts
    const numOfGroups = resp.totalGroups

    StoreInstances.uiStore.displaySuccessNotification(
      'itemsAddedToGroupMsg',
      null,
      [numOfItems, numOfGroups]
    )
  }

  private mapToCachedPart(part: Part): CachedPart {
    const lineCode = part.lineCode !== 'unknown' ? part.lineCode : ''
    return {
      groupName: part.group,
      partNumber: part.partNum,
      lineCode,
      stockQty: part.qty,
      comment: part.comments,
      description: part.description,
      validationStatus: part.validationStatus,
      isDuplicate: part.isDuplicate,
    }
  }

  private async insertNewParts(parts: Part[]): Promise<Part[]> {
    if (!parts?.length) return []

    const cachedParts = parts.map((p) => this.mapToCachedPart(p))

    const updatedParts = await ListValidationServiceProvider.addNewParts(
      this._validatedListBinId,
      cachedParts
    )
    const newParts: Part[] = updatedParts?.map((p) => this.mapValidatedPart(p))

    return newParts
  }
}

export const AddToListStoreContext = createContext<AddToListStore>(undefined)

export const useAddToListStore = (): AddToListStore => {
  return useContext(AddToListStoreContext)
}
